import { translateBackend } from '@assets/i18n/config';
import type { Account, AccountTypes } from '@interfaces/account';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { Typography } from 'hephaestus/web';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useAccountCreationMethodChoice = () => {
  type MethodChoiceResult = {
    choice: typeof selectedMethod;
    isTransferredFromAnotherInstitution: boolean;
    closeDialog: () => void;
  };
  const [resolver, setResolver] = useState<
    ((result: MethodChoiceResult) => void) | null
  >(null);
  const hasPending = !!resolver;

  /** Modal state */
  const [selectedMethod, setSelectedMethod] = useState<
    | { method: 'NEW_ACCOUNT' }
    | { method: 'NEW_SUB_ACCOUNT'; account: Account }
    | null
  >(null);
  const [accountType, setAccountType] = useState<AccountTypes | null>(null);
  const [activeAccounts, setActiveAccounts] = useState<Account[]>([]);
  const [allowNewAccount, setAllowNewAccount] = useState<boolean>(false);
  const [showRespTransferToggle, setShowRespTransferToggle] =
    useState<boolean>(false);
  const [
    isTransferredFromAnotherInstitution,
    setIsTransferredFromAnotherInstitution,
  ] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(!selectedMethod);
  const [disableCancelButton, setDisableCancelButton] =
    useState<boolean>(false);
  /** ************* */

  const { t } = useTranslation('workflowCompletions');
  const accountTypeDisplay = t(`accountTypes:${accountType}`);

  useEffect(() => {
    setLoading(!selectedMethod);
  }, [selectedMethod]);

  const closeDialog = () => {
    setDisableCancelButton(false);
    setResolver(null);
    setSelectedMethod(null);
    setAccountType(null);
    setAllowNewAccount(false);
    setShowRespTransferToggle(false);
    setIsTransferredFromAnotherInstitution(false);
  };

  const getAccountCreationMethodChoice = async (args: {
    accountType: AccountTypes;
    activeAccounts: Account[];
    allowNewAccount: boolean;
    showRespTransferToggle?: boolean;
  }) =>
    new Promise<MethodChoiceResult>((resolve) => {
      setAccountType(args.accountType);
      setActiveAccounts(args.activeAccounts);
      setResolver(() => resolve);
      setAllowNewAccount(args.allowNewAccount);
      setShowRespTransferToggle(args.showRespTransferToggle ?? false);
      setIsTransferredFromAnotherInstitution(false);
    });

  return {
    getAccountCreationMethodChoice,
    accountCreationMethodChoiceDialog: (
      <ConfirmationModal
        title={t('createAccountForGoal.accountCreationMethodChoice.title')}
        confirmButtonLabel={t(
          'createAccountForGoal.accountCreationMethodChoice.confirmButtonLabel',
        )}
        open={hasPending}
        confirmButtonColor='primary'
        onConfirm={() => {
          setDisableCancelButton(true);
          resolver?.({
            choice: selectedMethod,
            isTransferredFromAnotherInstitution,
            closeDialog,
          });
        }}
        onCancel={() => {
          resolver?.({
            choice: null,
            isTransferredFromAnotherInstitution: false,
            closeDialog,
          });
        }}
        loading={loading}
        disableCancelButton={disableCancelButton}
      >
        <Typography variant='bodyLarge'>
          {allowNewAccount
            ? t('createAccountForGoal.accountCreationMethodChoice.body', {
                numAccounts: activeAccounts?.length,
                accountType: accountTypeDisplay,
              })
            : t(
                'createAccountForGoal.accountCreationMethodChoice.bodyNoNewAccount',
                {
                  numAccounts: activeAccounts?.length,
                  accountType: accountTypeDisplay,
                },
              )}
        </Typography>
        <SelectionTile
          sx={{ mt: 2 }}
          type='radio'
          options={[
            ...activeAccounts.map((account) => ({
              label: t(
                'createAccountForGoal.accountCreationMethodChoice.newSubAccount',
                {
                  accountName: account.translatedNickName
                    ? translateBackend(account.translatedNickName)
                    : accountTypeDisplay,
                  accountId: `***${account.id.slice(-4)}`,
                },
              ),
              value: account.id,
            })),
            ...(allowNewAccount
              ? [
                  {
                    label: t(
                      'createAccountForGoal.accountCreationMethodChoice.newAccount',
                      { accountType: accountTypeDisplay },
                    ),
                    value: 'NEW_ACCOUNT',
                  },
                ]
              : []),
          ]}
          value={
            selectedMethod?.method === 'NEW_ACCOUNT'
              ? 'NEW_ACCOUNT'
              : (selectedMethod?.account?.id as string)
          }
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            if (e.target.value === 'NEW_ACCOUNT') {
              setSelectedMethod({ method: 'NEW_ACCOUNT' });
              // A brand-new account has no persisted transfer flag yet.
              setIsTransferredFromAnotherInstitution(false);
            } else {
              const account = activeAccounts.find(
                (item) => item.id === e.target.value,
              ) as Account;
              setSelectedMethod({
                method: 'NEW_SUB_ACCOUNT',
                account,
              });
              // Reflect the chosen account's current flag so an unchanged confirm is a
              // no-op and never clobbers an existing transfer selection.
              setIsTransferredFromAnotherInstitution(
                account?.isTransferredFromAnotherInstitution ?? false,
              );
            }
          }}
        />
        {showRespTransferToggle && selectedMethod && (
          <SwitchField
            data-testid='resp-transferred-from-another-institution'
            label={t('respAccountCreation.transferredFromAnotherInstitution')}
            checked={isTransferredFromAnotherInstitution}
            onChange={setIsTransferredFromAnotherInstitution}
            sx={{ mt: 2 }}
          />
        )}
      </ConfirmationModal>
    ),
  };
};
